import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../NewInventory/Icon";
import useDebounce from "../../../../hooks/useDebouncs";
import "../../searchFilterBar.css";
import "../../poDropdownOption.css";

const SCROLL_THRESHOLD_PX = 48;
const SEARCH_DEBOUNCE_MS = 300;

const optionRow = {
  width: "100%",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: 6,
  minHeight: 32,
  padding: "4px 10px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 12.5,
  color: "var(--qv-tx-0f1115, #0f1115)",
  fontFamily: "CircularMedium",
};

/**
 * Searchable filter dropdown — same chrome as the PO listing Vendors picker.
 */
const PickerFilterDropdown = ({
  value = "",
  onChange,
  items = [],
  selectedItem = null,
  allLabel = "All",
  showAllOption = true,
  searchPlaceholder = "Search",
  emptyLabel = "No matches.",
  icon = "box",
  disabled = false,
  loading = false,
  loadingLabel = "Loading...",
  listLoading = false,
  onSearchChange,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  menuMaxHeight = 360,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const serverSearch = Boolean(onSearchChange);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const selected =
    (selectedItem && String(selectedItem.id) === String(value) && selectedItem) ||
    items.find((item) => String(item.id) === String(value));
  const hasValue = Boolean(value);
  const displayLabel = loading ? loadingLabel : selected?.label || allLabel;

  useEffect(() => {
    if (!serverSearch) return;
    onSearchChange(debouncedQuery);
  }, [debouncedQuery, onSearchChange, serverSearch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (serverSearch) {
          setQuery("");
          onSearchChange("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onSearchChange, serverSearch]);

  useEffect(() => {
    if (loading) setOpen(false);
  }, [loading]);

  const filtered = useMemo(() => {
    if (serverSearch) return items;
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      const hay = [item.label, item.sublabel, item.badge]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, query, serverSearch]);

  const isSearchDebouncing =
    serverSearch && query.trim() !== debouncedQuery.trim();
  const showListLoading =
    (query.trim() && (isSearchDebouncing || (listLoading && !loadingMore))) ||
    (!query.trim() && listLoading && !loadingMore && filtered.length === 0);

  const handleListScroll = (e) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    if (
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - SCROLL_THRESHOLD_PX
    ) {
      onLoadMore();
    }
  };

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery("");
    if (serverSearch) onSearchChange("");
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => {
      if (prev && serverSearch) {
        setQuery("");
        onSearchChange("");
      }
      return !prev;
    });
  };

  return (
    <div
      ref={ref}
      className="po-picker-filter"
      style={{
        position: "relative",
        flex: "1 1 180px",
        minWidth: 160,
        zIndex: open ? 80 : "auto",
        ...style,
      }}
    >
      <button
        type="button"
        className={`po-picker-filter-trigger${hasValue || loading ? "" : " is-empty"}`}
        disabled={disabled || loading}
        onClick={handleToggle}
        style={{
          height: 36,
          padding: "0 12px",
          width: "100%",
          boxSizing: "border-box",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 8,
          cursor: disabled || loading ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "CircularMedium",
        }}
      >
        <Icon name={icon} size={13} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: loading ? "var(--qv-tx-7a818f, #7a818f)" : "inherit",
          }}
        >
          {displayLabel}
        </span>
        <Icon name="chevron-down" size={10} />
      </button>
      {open && !loading ? (
        <div
          className="po-vendor-picker-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            width: "100%",
            zIndex: 81,
            background: "var(--qv-bg-ffffff, #fff)",
            border: "1px solid var(--qv-bd-e3e6eb, #e3e6eb)",
            borderRadius: 10,
            boxShadow: "0 18px 40px var(--qv-shadow-a15x17x21x120, rgba(15,17,21,0.12))",
            maxHeight: menuMaxHeight,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div style={{ padding: 8, borderBottom: "1px solid var(--qv-bd-eef0f3, #eef0f3)", flexShrink: 0 }}>
            <div className="po-vendor-search">
              <Icon name="search" size={13} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 12.5,
                  color: "var(--qv-tx-0f1115, #0f1115)",
                  fontFamily: "inherit",
                }}
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--qv-tx-7a818f, #7a818f)",
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  <Icon name="close" size={11} />
                </button>
              ) : null}
            </div>
          </div>
          <div
            style={{ flex: 1, overflowY: "auto", padding: 4 }}
            onScroll={handleListScroll}
          >
            {showAllOption ? (
              <>
                <button
                  type="button"
                  onClick={() => pick("")}
                  className={`po-dropdown-option${!value ? " po-dropdown-option--active" : ""}`}
                  style={optionRow}
                >
                  <span style={{ flex: 1 }}>{allLabel}</span>
                  {!value ? (
                    <Icon name="check" size={13} color="var(--qv-brand-text)" />
                  ) : null}
                </button>
                <div style={{ height: 1, background: "var(--qv-bg-eef0f3, #eef0f3)", margin: "4px 0" }} />
              </>
            ) : null}
            {showListLoading ? (
              <div
                style={{
                  padding: "16px 10px",
                  textAlign: "center",
                  fontSize: 12.5,
                  color: "var(--qv-tx-7a818f, #7a818f)",
                }}
              >
                {loadingLabel}
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{
                  padding: "16px 10px",
                  textAlign: "center",
                  fontSize: 12.5,
                  color: "var(--qv-tx-7a818f, #7a818f)",
                }}
              >
                {query
                  ? `No matches for "${query}".`
                  : emptyLabel}
              </div>
            ) : (
              filtered.map((item) => {
                const active = String(item.id) === String(value);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => pick(item.id)}
                    className={`po-dropdown-option${active ? " po-dropdown-option--active" : ""}`}
                    style={optionRow}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.label}
                    </span>
                    {item.badge ? (
                      <span
                        style={{
                          fontSize: 10,
                          padding: "2px 6px",
                          borderRadius: 99,
                          background: "rgba(232,89,12,0.12)",
                          color: "var(--qv-tx-a14a00, #a14a00)",
                          fontWeight: 500,
                          fontFamily: "CircularMedium",
                          letterSpacing: 0.3,
                          flexShrink: 0,
                        }}
                      >
                        {item.badge}
                      </span>
                    ) : null}
                    {active ? (
                      <Icon name="check" size={13} color="var(--qv-brand-text)" />
                    ) : null}
                  </button>
                );
              })
            )}
            {loadingMore ? (
              <div
                style={{
                  padding: "10px",
                  textAlign: "center",
                  fontSize: 12,
                  color: "var(--qv-tx-7a818f, #7a818f)",
                }}
              >
                Loading more…
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PickerFilterDropdown;
